/** Automation / Workflow Rules Engine (/admin/automation). TRIGGER → CONDITION → ACTION, no code. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Sparkles, Play, History, Pencil, Trash2, Zap, FlaskConical } from "lucide-react";
import { RuleBuilder } from "./RuleBuilder";
import { RunHistory, TemplatesGallery } from "./AutomationPanels";
import { PRIO_CLS, RUN_CLS, fmtWhen, relWhen, condText, emptyRule } from "./automationUtils";

const Stat = ({ label, value, tone = "text-slate-900", testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={testId}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-mono text-xl font-bold ${tone}`}>{value ?? "—"}</div>
  </div>
);

function RuleRow({ r, cat, canManage, onToggle, onEdit, onRun, onHistory, onDelete, busy }) {
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60 align-top" data-testid={`rule-row-${r.id}`}>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Switch checked={!!r.enabled} disabled={!canManage || busy} onCheckedChange={(v) => onToggle(r, v)} data-testid={`rule-toggle-${r.id}`} />
          <span className={`text-[10px] font-mono uppercase ${r.enabled ? "text-emerald-600" : "text-slate-400"}`}>{r.enabled ? "On" : "Off"}</span>
        </div>
      </td>
      <td className="px-3 py-2.5 min-w-[260px]">
        <div className="text-[13px] font-semibold text-slate-900" data-testid={`rule-name-${r.id}`}>{r.name}</div>
        <div className="text-[11px] text-slate-500 mt-0.5"><span className="font-mono text-[10px] uppercase text-sky-600">IF</span> {r.trigger_label}
          {r.conditions?.length ? <span> · {r.conditions.map((c) => condText(c, cat.operators)).join(" AND ")}</span> : null}</div>
        <div className="text-[11px] text-slate-500"><span className="font-mono text-[10px] uppercase text-emerald-600">THEN</span> {r.action_labels.join(" + ")}</div>
      </td>
      <td className="px-3 py-2.5 text-[12px] text-slate-700 whitespace-nowrap">{r.owner_email || "—"}</td>
      <td className="px-3 py-2.5"><span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${PRIO_CLS[r.priority]}`}>{r.priority}</span></td>
      <td className="px-3 py-2.5 whitespace-nowrap text-[12px]">
        <div className="text-slate-700">{fmtWhen(r.last_run_at)}</div>
        {r.last_run_status && <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${RUN_CLS[r.last_run_status] || ""}`} data-testid={`rule-last-status-${r.id}`}>{r.last_run_status.replace("_", " ")}{r.last_run_summary ? ` · ${r.last_run_summary.fired} fired` : ""}</span>}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap text-[12px] text-slate-700">{r.enabled ? relWhen(r.next_run_at) : <span className="text-slate-400">paused</span>}<div className="text-[10px] text-slate-400">every {r.interval_minutes}m</div></td>
      <td className="px-3 py-2.5 text-[12px] font-mono text-slate-700">{r.run_count || 0}<div className="text-[10px] text-slate-400">{r.total_fired || 0} fired</div></td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Run history" onClick={() => onHistory(r)} data-testid={`rule-history-${r.id}`}><History className="h-3.5 w-3.5" /></Button>
          {canManage && <>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Run now" onClick={() => onRun(r)} disabled={busy} data-testid={`rule-run-${r.id}`}><Play className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => onEdit(r)} data-testid={`rule-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-600" title="Delete" onClick={() => onDelete(r)} data-testid={`rule-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </>}
        </div>
      </td>
    </tr>
  );
}

export default function AutomationEngine() {
  const [cat, setCat] = useState(null);
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({});
  const [denied, setDenied] = useState(false);
  const [builder, setBuilder] = useState(null);
  const [templates, setTemplates] = useState(false);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/automation/rules"); setRules(data.items); setStats(data.stats); };
  useEffect(() => {
    api.get("/automation/catalog").then(({ data }) => setCat(data)).catch(() => setDenied(true));
    load().catch(() => setDenied(true));
  }, []);
  useEffect(() => { const t = setInterval(() => load().catch(() => {}), 30000); return () => clearInterval(t); }, []);

  if (denied) return <div className="py-24 text-center text-sm text-slate-500" data-testid="automation-denied">Automation Engine is available to Suntronix staff only.</div>;
  if (!cat) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const canManage = cat.can_manage;

  const toggle = async (r, v) => {
    setBusy(true);
    try { await api.post(`/automation/rules/${r.id}/toggle`, { enabled: v }); toast.success(v ? `“${r.name}” enabled — first run within a minute` : `“${r.name}” disabled`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const runNow = async (r) => {
    setBusy(true);
    try { const { data } = await api.post(`/automation/rules/${r.id}/run`); toast.success(`Run ${data.status.replace("_", " ")} · scanned ${data.scanned} · matched ${data.matched} · fired ${data.fired}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Run failed"); } finally { setBusy(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete automation “${r.name}”? Run history is kept.`)) return;
    setBusy(true);
    try { await api.delete(`/automation/rules/${r.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-5" data-testid="automation-engine">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-sky-600">Administration · Enterprise OS</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><Zap className="h-6 w-6 text-amber-500" />Automation Engine</h1>
          <p className="text-sm text-slate-500 mt-0.5">TRIGGER → CONDITION → ACTION. Rules scan live records on a schedule and create notifications, tasks, approvals and escalations — no code, fully reversible.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistory({ id: "*", name: "All" })} data-testid="all-runs-btn"><History className="h-3.5 w-3.5 mr-1" />All runs</Button>
          <Button variant="outline" size="sm" onClick={() => setTemplates(true)} data-testid="templates-btn"><Sparkles className="h-3.5 w-3.5 mr-1" />Templates</Button>
          {canManage && <Button size="sm" onClick={() => setBuilder(emptyRule())} data-testid="new-rule-btn"><Plus className="h-3.5 w-3.5 mr-1" />New automation</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat label="Automations" value={stats.total} testId="stat-total" />
        <Stat label="Enabled" value={stats.enabled} tone="text-emerald-700" testId="stat-enabled" />
        <Stat label="Runs today" value={stats.runs_today} testId="stat-runs" />
        <Stat label="Fired today" value={stats.fired_today} tone="text-sky-700" testId="stat-fired" />
        <Stat label="Failed today" value={stats.failed_today} tone={stats.failed_today ? "text-rose-700" : "text-slate-900"} testId="stat-failed" />
        <Stat label="Open tasks" value={stats.open_tasks} tone="text-amber-700" testId="stat-tasks" />
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-left" data-testid="rules-table">
          <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>{["Enabled", "Automation", "Owner", "Priority", "Last run", "Next run", "Runs", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rules.map((r) => <RuleRow key={r.id} r={r} cat={cat} canManage={canManage} busy={busy} onToggle={toggle} onEdit={(x) => setBuilder({ ...emptyRule(), ...x })} onRun={runNow} onHistory={setHistory} onDelete={del} />)}
            {rules.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-14 text-center" data-testid="rules-empty">
                <FlaskConical className="h-8 w-8 mx-auto text-slate-300" />
                <div className="mt-2 text-sm font-semibold text-slate-700">No automations yet</div>
                <div className="text-[12px] text-slate-500">Start from a template (e.g. “Quote idle for 3 days → notify salesperson”) or build your own.</div>
                <div className="mt-3 flex justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setTemplates(true)} data-testid="empty-templates-btn"><Sparkles className="h-3.5 w-3.5 mr-1" />Browse templates</Button>
                  {canManage && <Button size="sm" onClick={() => setBuilder(emptyRule())} data-testid="empty-new-btn"><Plus className="h-3.5 w-3.5 mr-1" />New automation</Button>}
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {builder && <RuleBuilder open onClose={() => setBuilder(null)} cat={cat} initial={builder} onSaved={() => { setBuilder(null); load(); }} />}
      <TemplatesGallery open={templates} onClose={() => setTemplates(false)} cat={cat} canManage={canManage} onCreated={() => { setTemplates(false); load(); }} />
      <RunHistory rule={history} onClose={() => setHistory(null)} />
    </div>
  );
}
